// Localized market configs for Lumotis per-country digital marketing homepages.
// Each config drives SEO meta, Hero copy, currency, local channels, insights and testimonials.

export const MARKETS_CONFIG = {
  us: {
    code: "us",
    name: "United States",
    flag: "🇺🇸",
    shortName: "USA",
    locale: "en_US",
    currency: "$",
    multiplier: 1,
    metaTitle:
      "Digital Marketing Agency USA — Google Ads, SEO & AI Search | Lumotis",
    metaDescription:
      "Lumotis is a full-stack digital marketing agency serving US businesses. We grow revenue with Google Ads, Meta Ads, SEO, email marketing and AI search visibility. Free audit.",
    keywords:
      "digital marketing agency USA, Google Ads agency USA, SEO agency United States, Meta Ads agency USA, email marketing agency USA, AI search optimization USA",
    h1Title: "The Full-Stack",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "US businesses trust to turn traffic into revenue.",
    heroSubtitle:
      "Lumotis helps US businesses grow revenue with Google Ads, Meta Ads, SEO, email marketing, and AI search visibility — all coordinated under one roof. From NYC startups to LA brands, we build the growth engine that compounds.",
    marketEmphasis: "$250B+ digital ad market · Google + Meta dominant · AI search accelerating",
    trustBadge: "Serving 40+ US businesses · California → New York",
    verticalFocus: ["Ecommerce", "SaaS", "Real Estate"],
    localStat: { value: "40+", label: "US clients served" },
    localChannels: ["Google Ads", "Meta Ads", "SEO", "Email & SMS", "AI Search (GEO)"],
    localInsights: [
      {
        stat: "93%",
        label: "of US purchase decisions start with an online search",
        desc: "Appearing on page 1 of Google — in both paid and organic — is the single most important growth lever for any US business.",
      },
      {
        stat: "4.8×",
        label: "average ROAS achievable on Google Shopping for US ecommerce",
        desc: "US consumers have the highest purchase intent of any market. The right Google Shopping structure + Meta retargeting compounds this dramatically.",
      },
      {
        stat: "45%",
        label: "of US consumers now use AI chatbots to find products and services",
        desc: "GEO/AEO (AI Search Optimization) is the fastest-growing new channel in the US — and most businesses haven't even started yet.",
      },
    ],
    testimonial: {
      name: "Daniel Brooks",
      role: "CEO · Skydeck (San Francisco, CA)",
      text: "Lumotis rebuilt our entire paid + SEO stack in 60 days. Google ROAS went from 1.9× to 4.3× and organic traffic doubled. Best agency investment we've made.",
    },
  },

  uk: {
    code: "uk",
    name: "United Kingdom",
    flag: "🇬🇧",
    shortName: "UK",
    locale: "en_GB",
    currency: "£",
    multiplier: 0.8,
    metaTitle:
      "Digital Marketing Agency UK — Google Ads, SEO & Social Media | Lumotis",
    metaDescription:
      "Lumotis is a full-stack digital marketing agency for UK businesses. We drive revenue with Google Ads, SEO, Meta Ads, email marketing and AI search. Free marketing audit.",
    keywords:
      "digital marketing agency UK, Google Ads agency London, SEO agency UK, Meta Ads agency UK, social media agency UK, email marketing agency London",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "UK businesses trust to grow revenue online.",
    heroSubtitle:
      "Lumotis helps UK businesses grow with Google Ads, SEO, Meta Ads, email marketing and AI search visibility — fully GDPR compliant. From London FinTechs to Manchester retailers, we build the engine that fills pipelines.",
    marketEmphasis: "£25B+ UK digital ad market · GDPR-compliant · Privacy-first targeting",
    trustBadge: "Serving UK businesses · London, Manchester, Birmingham",
    verticalFocus: ["Finance", "Retail", "Professional Services"],
    localStat: { value: "22+", label: "UK clients served" },
    localChannels: ["Google Ads", "Meta Ads", "SEO", "LinkedIn Ads", "Email Marketing"],
    localInsights: [
      {
        stat: "88%",
        label: "of UK consumers research online before buying",
        desc: "UK buyers are research-heavy. Content marketing, authoritative SEO and Google review management all compound into measurable revenue growth.",
      },
      {
        stat: "£2.50",
        label: "average CPC on Google Search in the UK — 20% lower than USA",
        desc: "The UK Google Ads market offers strong intent signals at lower cost than the US. Well-structured campaigns here consistently outperform US benchmarks on efficiency.",
      },
      {
        stat: "61%",
        label: "of UK businesses cite SEO as their top digital marketing channel",
        desc: "Organic search dominates in the UK. A combined paid + SEO + email approach closes the loop — capturing demand at every stage of the funnel.",
      },
    ],
    testimonial: {
      name: "Amelia Carter",
      role: "Head of Growth · Halo Health (London)",
      text: "Lumotis rebuilt our Google Ads structure and launched a new SEO strategy. Organic traffic up 3× and paid CAC down 38% in four months. Brilliant team.",
    },
  },

  au: {
    code: "au",
    name: "Australia",
    flag: "🇦🇺",
    shortName: "Australia",
    locale: "en_AU",
    currency: "A$",
    multiplier: 1.55,
    metaTitle:
      "Digital Marketing Agency Australia — Google Ads, SEO & Social Media | Lumotis",
    metaDescription:
      "Lumotis is a full-stack digital marketing agency for Australian businesses. We grow revenue with Google Ads, Local SEO, Instagram and email marketing. Free marketing audit.",
    keywords:
      "digital marketing agency Australia, Google Ads agency Sydney, SEO agency Melbourne, social media agency Australia, email marketing agency AU",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "Australian businesses trust to grow revenue fast.",
    heroSubtitle:
      "Lumotis helps Australian businesses grow with Google Ads, Local SEO, Instagram, email marketing and AI search visibility. From Sydney startups to Melbourne hospitality brands — we build the marketing engine that fills pipelines.",
    marketEmphasis: "A$13B+ digital ad market · Instagram-heavy · Growing startup scene",
    trustBadge: "Serving AU businesses · Sydney, Melbourne, Brisbane",
    verticalFocus: ["Hospitality", "Real Estate", "Healthcare"],
    localStat: { value: "14+", label: "AU clients served" },
    localChannels: ["Google Ads", "Instagram Ads", "Local SEO", "Email Marketing", "Google Shopping"],
    localInsights: [
      {
        stat: "91%",
        label: "of Australians use Google as their primary search engine",
        desc: "Google dominates in Australia even more than in the US. Local SEO and Google Ads are the two highest-ROI channels for most AU businesses.",
      },
      {
        stat: "79%",
        label: "of Australian adults use Instagram — the highest rate in Asia-Pacific",
        desc: "Instagram is where Australian consumers discover brands, especially in hospitality, beauty, fitness and retail. Visual content + Instagram Ads is a core growth lever.",
      },
      {
        stat: "35%",
        label: "less competition on Google Ads AU vs UK for equivalent search terms",
        desc: "Lower competition means lower CPCs and faster ROAS results. Australian markets consistently deliver faster paid media wins than comparable UK or US campaigns.",
      },
    ],
    testimonial: {
      name: "Liam O'Connor",
      role: "Founder · Voyage (Sydney)",
      text: "Lumotis built our full marketing stack — Google Ads, Instagram and Local SEO. Bookings up 45% in 60 days and our Google Maps ranking went from nowhere to #1 in the city.",
    },
  },

  uae: {
    code: "uae",
    name: "UAE",
    flag: "🇦🇪",
    shortName: "UAE",
    locale: "en_AE",
    currency: "AED ",
    multiplier: 3.67,
    metaTitle:
      "Digital Marketing Agency Dubai & UAE — Google Ads, Instagram & AI Search | Lumotis",
    metaDescription:
      "Lumotis is a full-stack digital marketing agency for Dubai and UAE businesses. Google Ads, Instagram, Snapchat, WhatsApp marketing and AI search. Free marketing audit.",
    keywords:
      "digital marketing agency Dubai, Google Ads agency UAE, social media agency Dubai, Instagram marketing UAE, WhatsApp marketing Dubai, SEO agency UAE",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "Dubai and UAE businesses trust for high-CPM growth.",
    heroSubtitle:
      "Lumotis helps UAE businesses grow with Google Ads, Instagram, Snapchat, WhatsApp Business marketing and AI search visibility. Dubai's luxury brands, real estate developers and hospitality groups — we build the marketing engine that drives premium leads.",
    marketEmphasis: "Highest CPMs in world · Luxury brands · Instagram + Snapchat dominant",
    trustBadge: "Serving UAE businesses · Dubai & Abu Dhabi",
    verticalFocus: ["Real Estate", "Luxury Retail", "Hospitality"],
    localStat: { value: "18+", label: "UAE clients served" },
    localChannels: ["Google Ads", "Instagram Ads", "Snapchat Ads", "WhatsApp Business", "Influencer Marketing"],
    localInsights: [
      {
        stat: "99%",
        label: "internet penetration in UAE — the highest in the Middle East",
        desc: "UAE consumers are among the most digitally active in the world. With virtually universal internet access, digital marketing reaches the entire addressable market.",
      },
      {
        stat: "4.7×",
        label: "higher average CPM in UAE vs global average on Instagram",
        desc: "The UAE Instagram audience has exceptional purchasing power. Higher CPMs are offset by premium conversion rates and dramatically higher average order values.",
      },
      {
        stat: "72%",
        label: "of UAE residents use WhatsApp daily — making it the #1 business channel",
        desc: "WhatsApp Business API is the most direct channel to UAE consumers. Automated lead follow-up, appointment reminders and broadcast campaigns outperform email 3× in the region.",
      },
    ],
    testimonial: {
      name: "Omar Al-Farsi",
      role: "Founder · PaySprint (Dubai)",
      text: "Lumotis built our full digital marketing strategy — Google Ads, Instagram and WhatsApp automation. Qualified leads tripled in 90 days and our cost per lead dropped 44%. Exceptional results for Dubai.",
    },
  },

  ca: {
    code: "ca",
    name: "Canada",
    flag: "🇨🇦",
    shortName: "Canada",
    locale: "en_CA",
    currency: "C$",
    multiplier: 1.36,
    metaTitle:
      "Digital Marketing Agency Canada — Bilingual EN + FR Google Ads & SEO | Lumotis",
    metaDescription:
      "Lumotis is a full-stack digital marketing agency for Canadian businesses. Bilingual EN + FR Google Ads, SEO, Meta Ads and email marketing. Free marketing audit.",
    keywords:
      "digital marketing agency Canada, Google Ads agency Toronto, SEO agency Canada, bilingual marketing Canada, French SEO Quebec, Meta Ads Canada",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "Canadian businesses trust for bilingual EN + FR growth.",
    heroSubtitle:
      "Lumotis helps Canadian businesses grow with Google Ads, Meta Ads, bilingual English + French SEO, and email marketing. From Toronto tech companies to Montreal agencies — we build the marketing engine that fills pipelines coast to coast.",
    marketEmphasis: "C$15B+ digital market · Bilingual EN + FR · Strong tech sector growth",
    trustBadge: "Serving Canadian businesses · Toronto, Montreal, Vancouver",
    verticalFocus: ["Tech/SaaS", "Finance", "Healthcare"],
    localStat: { value: "11+", label: "CA clients served" },
    localChannels: ["Google Ads", "Meta Ads", "Bilingual SEO (EN + FR)", "Email Marketing", "LinkedIn Ads"],
    localInsights: [
      {
        stat: "96%",
        label: "of Canadians use the internet daily — among the world's highest rates",
        desc: "Canada's highly connected population makes digital marketing exceptionally effective. Strong mobile and social media adoption drives consistent results across channels.",
      },
      {
        stat: "2×",
        label: "more organic clicks from bilingual EN + FR SEO vs English-only",
        desc: "Quebec represents 23% of Canada's population and searches primarily in French. Bilingual content and French keyword targeting unlock a market most English-only agencies miss entirely.",
      },
      {
        stat: "22%",
        label: "lower Google Ads CPCs in Canada vs USA for equivalent intent keywords",
        desc: "Similar purchasing power, less competition — Canadian Google Ads campaigns consistently outperform US benchmarks on cost-efficiency.",
      },
    ],
    testimonial: {
      name: "Sophie Tremblay",
      role: "CMO · MapleFin (Montreal)",
      text: "Lumotis built our bilingual marketing strategy — English and French Google Ads, SEO and email. Quebec leads up 3× in one quarter. Finally an agency that understands the full Canadian market.",
    },
  },

  de: {
    code: "de",
    name: "Germany",
    flag: "🇩🇪",
    shortName: "Germany",
    locale: "de_DE",
    currency: "€",
    multiplier: 0.93,
    metaTitle:
      "Digital Marketing Agency Germany — Google Ads, SEO & Email Marketing | Lumotis",
    metaDescription:
      "Lumotis ist eine Full-Stack Digital-Marketing-Agentur für deutsche Unternehmen. Google Ads, SEO, E-Mail-Marketing und KI-Suchoptimierung. Kostenloses Marketing-Audit.",
    keywords:
      "digital marketing agency Germany, Google Ads Agentur Deutschland, SEO Agentur Deutschland, E-Mail Marketing Agentur DE, social media Agentur Deutschland",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "German businesses trust to grow revenue across the DACH market.",
    heroSubtitle:
      "Lumotis helps German businesses grow with Google Ads, organic SEO, email marketing and GDPR-compliant automation. From Berlin FinTechs to Munich B2B brands — we build the marketing engine that compounds through Germany's search-dominant market.",
    marketEmphasis: "€35B+ DACH digital market · Organic-search-first · GDPR-compliant stack",
    trustBadge: "Serving German businesses · Berlin, Munich, Hamburg",
    verticalFocus: ["B2B", "Automotive", "E-commerce"],
    localStat: { value: "9+", label: "DACH clients served" },
    localChannels: ["Google Ads", "SEO (German)", "Email Marketing", "LinkedIn DACH", "GDPR-compliant automation"],
    localInsights: [
      {
        stat: "78%",
        label: "of German consumers research extensively online before any purchase",
        desc: "Germans are the most research-intensive buyers in Europe. Content marketing, technical SEO and authoritative review profiles are essential — not optional.",
      },
      {
        stat: "40%",
        label: "less paid ad competition in Germany vs UK for comparable B2B keywords",
        desc: "Germany is dominated by organic search. Smart businesses combine strong SEO with selective, high-intent Google Ads to capture ready buyers at lowest possible cost.",
      },
      {
        stat: "€287",
        label: "average annual revenue generated per German email subscriber",
        desc: "German consumers are loyal and high-value once acquired. Email marketing — when GDPR-compliant and value-driven — delivers the highest ROI of any channel in DACH.",
      },
    ],
    testimonial: {
      name: "Lukas Weber",
      role: "Gründer · Kindred (Berlin)",
      text: "Lumotis versteht den deutschen Markt besser als jede Agentur, mit der wir bisher gearbeitet haben. Google Ads ROAS von 2.1× auf 4.8× in 90 Tagen. Klare Empfehlung.",
    },
  },

  jp: {
    code: "jp",
    name: "Japan",
    flag: "🇯🇵",
    shortName: "Japan",
    locale: "ja_JP",
    currency: "¥",
    multiplier: 150,
    metaTitle:
      "Digital Marketing Agency Japan — Google Ads, LINE & Yahoo Japan SEO | Lumotis",
    metaDescription:
      "Lumotis is a full-stack digital marketing agency for Japanese businesses. Google Ads, LINE Ads, Yahoo Japan, Instagram and Japanese SEO. Free marketing audit.",
    keywords:
      "digital marketing agency Japan, Google Ads agency Tokyo, LINE marketing Japan, Yahoo Japan SEO, Instagram marketing Japan, digital marketing Tokyo",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "Japanese businesses trust for LINE, Google and AI-driven growth.",
    heroSubtitle:
      "Lumotis helps Japanese businesses grow with Google Ads, LINE Ads, Yahoo Japan advertising, Instagram and Japanese-language SEO. From Tokyo e-commerce brands to Osaka retail chains — we build the marketing engine tuned to Japan's unique digital landscape.",
    marketEmphasis: "¥7T+ digital ad market · LINE dominant · Yahoo Japan still significant",
    trustBadge: "Serving Japanese businesses · Tokyo & Osaka",
    verticalFocus: ["E-commerce", "Gaming", "Fashion"],
    localStat: { value: "7+", label: "JP clients served" },
    localChannels: ["Google Ads (JP)", "LINE Ads", "Yahoo Japan Ads", "Instagram", "Japanese SEO"],
    localInsights: [
      {
        stat: "92M",
        label: "active LINE users in Japan — the #1 marketing channel for consumer brands",
        desc: "LINE is Japan's WhatsApp. LINE Official Accounts, LINE Ads and broadcast messages reach more Japanese consumers than any other single digital channel.",
      },
      {
        stat: "30%",
        label: "of Japanese search queries still go through Yahoo Japan — not Google",
        desc: "Japan is the only major market where Yahoo remains a dominant search engine. Effective Japanese SEO and SEM requires optimisation across both Google and Yahoo Japan.",
      },
      {
        stat: "¥4.2M",
        label: "average Japanese consumer lifetime value — 2× the global average",
        desc: "Japanese consumers are extraordinarily loyal once trust is established. Retention marketing and premium brand positioning compound dramatically over time in this market.",
      },
    ],
    testimonial: {
      name: "Haruki Sato",
      role: "Founder · LoopQuest JP (Tokyo)",
      text: "Lumotis brought real Japanese market expertise — LINE strategy, Yahoo Japan campaigns, and Japanese SEO. Revenue doubled within one quarter. They understand Japan in a way most agencies don't.",
    },
  },

  sg: {
    code: "sg",
    name: "Singapore",
    flag: "🇸🇬",
    shortName: "Singapore",
    locale: "en_SG",
    currency: "S$",
    multiplier: 1.35,
    metaTitle:
      "Digital Marketing Agency Singapore — Google Ads, LinkedIn & SEA Growth | Lumotis",
    metaDescription:
      "Lumotis is a full-stack digital marketing agency for Singapore and Southeast Asia businesses. Google Ads, LinkedIn, Meta Ads, WhatsApp and SEA expansion. Free audit.",
    keywords:
      "digital marketing agency Singapore, Google Ads agency Singapore, LinkedIn marketing Singapore, SEA marketing agency, WhatsApp Business Singapore, social media agency SG",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "Singapore businesses trust to grow across Southeast Asia.",
    heroSubtitle:
      "Lumotis helps Singapore businesses grow with Google Ads, LinkedIn, Meta Ads, WhatsApp Business marketing and AI search visibility. From B2B SaaS companies to hospitality brands — we build the marketing engine that scales across the SEA region.",
    marketEmphasis: "S$3.5B+ digital market · SEA hub · English + Chinese bilingual",
    trustBadge: "Serving SG businesses · Orchard, Tanjong Pagar, CBD",
    verticalFocus: ["B2B/SaaS", "Finance", "Hospitality"],
    localStat: { value: "8+", label: "SG clients served" },
    localChannels: ["Google Ads", "LinkedIn Ads", "Meta Ads", "WhatsApp Business API", "SEA region SEO"],
    localInsights: [
      {
        stat: "88%",
        label: "of Singapore businesses use digital marketing as their primary growth channel",
        desc: "Singapore has one of the world's most sophisticated digital marketing ecosystems. High English literacy, strong mobile adoption and excellent ad infrastructure make every channel highly efficient.",
      },
      {
        stat: "3.2×",
        label: "higher B2B lead quality from LinkedIn vs Google Ads in Singapore",
        desc: "Singapore's strong B2B and financial services sector makes LinkedIn the premium lead generation channel. Proper LinkedIn targeting consistently outperforms Google for enterprise sales cycles.",
      },
      {
        stat: "S$2.8B",
        label: "projected digital advertising spend in Singapore by 2027",
        desc: "Singapore is Southeast Asia's digital marketing hub. Businesses that build strong digital presence now capture disproportionate market share as the SEA economy grows.",
      },
    ],
    testimonial: {
      name: "Mei Lin",
      role: "CEO · FinKart (Singapore)",
      text: "Lumotis built our full Singapore and SEA marketing stack — Google Ads, LinkedIn and WhatsApp automation. B2B pipeline grew 3× in 10 weeks. Best decision we made for our SEA expansion.",
    },
  },

  br: {
    code: "br",
    name: "Brazil",
    flag: "🇧🇷",
    shortName: "Brazil",
    locale: "pt_BR",
    currency: "R$",
    multiplier: 5.1,
    metaTitle:
      "Digital Marketing Agency Brazil — Instagram, WhatsApp & Google Ads | Lumotis",
    metaDescription:
      "Lumotis é uma agência de marketing digital full-stack para empresas brasileiras. Instagram Ads, WhatsApp marketing, Google Ads e SEO em português. Auditoria gratuita.",
    keywords:
      "agência de marketing digital Brasil, Google Ads agência São Paulo, Instagram marketing Brasil, WhatsApp marketing empresas, SEO em português, marketing digital Rio",
    h1Title: "The",
    h1Accent: "Digital Marketing Agency",
    h1Tail: "Brazilian businesses trust for Instagram, WhatsApp and Google growth.",
    heroSubtitle:
      "Lumotis helps Brazilian businesses grow with Instagram Ads, WhatsApp Business marketing, Google Ads and Portuguese-language SEO. From São Paulo e-commerce brands to Rio hospitality groups — we build the marketing engine tuned to Brazil's social-first market.",
    marketEmphasis: "R$55B+ digital market · Instagram #1 channel · WhatsApp essential for sales",
    trustBadge: "Serving Brazilian businesses · São Paulo, Rio de Janeiro, Belo Horizonte",
    verticalFocus: ["E-commerce", "Food & Delivery", "Fashion"],
    localStat: { value: "6+", label: "BR clients served" },
    localChannels: ["Instagram Ads", "WhatsApp Business API", "Google Ads (PT)", "TikTok Ads", "Portuguese SEO"],
    localInsights: [
      {
        stat: "134M",
        label: "Brazilians use Instagram — the largest Instagram market in Latin America",
        desc: "Brazil leads Latin America in Instagram usage. Instagram Ads, Stories and Reels are the primary discovery channels for consumer brands across every category.",
      },
      {
        stat: "98%",
        label: "of Brazilian smartphone users have WhatsApp installed",
        desc: "WhatsApp Business is not optional in Brazil — it's the primary sales and customer service channel. Automated WhatsApp flows convert leads 4× better than email in the Brazilian market.",
      },
      {
        stat: "40%",
        label: "annual growth in Brazilian e-commerce — the fastest growing in Latin America",
        desc: "Brazil's e-commerce market is expanding rapidly. Businesses that combine Instagram content, WhatsApp follow-up and Google Shopping dominate category growth.",
      },
    ],
    testimonial: {
      name: "Rafael Costa",
      role: "Fundador · PlayLoop BR (São Paulo)",
      text: "Lumotis construiu toda a nossa estratégia de marketing — Instagram, WhatsApp e Google Ads em português. Nossas vendas triplicaram em 90 dias. A melhor decisão que tomamos.",
    },
  },

  in: {
    code: "in",
    name: "India",
    flag: "🇮🇳",
    shortName: "India",
    locale: "en_IN",
    currency: "₹",
    multiplier: 84,
    metaTitle:
      "Digital Marketing Agency India — Google Ads, WhatsApp & Hindi SEO | Lumotis",
    metaDescription:
      "Lumotis is India's digital marketing agency, headquartered in Jaipur. Google Ads, Meta Ads, WhatsApp marketing, YouTube and Hindi + English SEO. Free marketing audit.",
    keywords:
      "digital marketing agency India, Google Ads agency Mumbai, SEO agency Bangalore, WhatsApp marketing India, Hindi SEO, social media agency India, marketing agency Jaipur",
    h1Title: "India's",
    h1Accent: "Full-Stack Digital Marketing Agency",
    h1Tail: "built in Jaipur, growing businesses nationwide.",
    heroSubtitle:
      "Lumotis is India's digital marketing agency, headquartered in Jaipur. We help Indian businesses grow with Google Ads, Meta Ads, WhatsApp marketing, YouTube and bilingual Hindi + English SEO — across Mumbai, Delhi, Bangalore, Jaipur and every city in between.",
    marketEmphasis: "₹580B+ digital ad market · Lowest CPCs globally · WhatsApp + YouTube dominant",
    trustBadge: "Trusted by Indian businesses · Mumbai, Delhi, Bangalore, Jaipur",
    verticalFocus: ["EdTech", "E-commerce", "Healthcare"],
    localStat: { value: "35+", label: "IN clients served" },
    localChannels: ["Google Ads (Hindi + EN)", "Meta Ads", "WhatsApp Business API", "YouTube Ads", "Hindi + English SEO"],
    localInsights: [
      {
        stat: "650M+",
        label: "Indian internet users — the world's second-largest online population",
        desc: "India's digital market is vast and growing at 15% per year. Businesses that establish strong digital presence now capture disproportionate market share as the middle class expands.",
      },
      {
        stat: "8×",
        label: "lower average Google Ads CPC in India vs USA for equivalent intent",
        desc: "India offers some of the lowest CPCs of any major digital ad market. Properly structured Google Ads campaigns here deliver exceptional ROAS — especially for education, healthcare and e-commerce.",
      },
      {
        stat: "487M",
        label: "WhatsApp users in India — the world's largest WhatsApp market",
        desc: "WhatsApp Business is India's most effective direct marketing channel. Automated lead follow-up, appointment reminders and promotional broadcasts consistently outperform email by 5× in India.",
      },
    ],
    testimonial: {
      name: "Priya Iyer",
      role: "Founder · MealFox (Mumbai)",
      text: "Lumotis built our complete digital marketing strategy — Google Ads, Meta, WhatsApp automation and Hindi SEO. Leads up 4× in 90 days at half the cost per acquisition. The best agency in India for full-stack digital marketing.",
    },
  },
};

export const MARKET_CODES = Object.keys(MARKETS_CONFIG);
